import type { PerformanceResourceItem } from '@/app/lib/api/types-api'
import {
  DEFAULT_CURRENCY,
  FRACTION_DIGITS,
  PRICE_LOCALE,
} from '@/app/components/atoms/Price/constants'
import { extraText, resolveArtistLinks } from '@/app/lib/performance'
import {
  EXTRA_AGES,
  EXTRA_GATES,
  EXTRA_HANDICAPPED,
  EXTRA_SHOW_LENGTH,
} from '@/app/lib/performance/constants'
import { resolveProductGenre, toPlainText } from '@/app/lib/utils'

import { FAQ_ARTISTA_FALLBACK_TO_TITLE } from './constants'
import { buildPickupList } from './facts/buildPickupList'
import { formatFaqDateRange } from './facts/formatFaqDateRange'
import { parseAccessibility } from './facts/parseAccessibility'
import { parseAge } from './facts/parseAge'
import { parseGates } from './facts/parseGates'
import type { FaqCategory, FaqFacts } from './types'

/** Formatea un importe con el mismo criterio que el resto de la ficha (`Price`). */
function formatPriceDesde(amount: number): string {
  return new Intl.NumberFormat(PRICE_LOCALE, {
    style: 'currency',
    currency: DEFAULT_CURRENCY,
    minimumFractionDigits: FRACTION_DIGITS,
    maximumFractionDigits: FRACTION_DIGITS,
  }).format(amount)
}

/** Texto recortado o `null` si queda vacío / no es texto. */
function clean(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/** Sin tildes y en minúsculas, para comparar texto libre ("Sí" → "si"). */
function normalize(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/**
 * Normaliza un extra afirmativo/negativo a `'si' | 'no'`. Texto ambiguo o
 * vacío ⇒ `null` (la plantilla se omite en vez de afirmar algo dudoso).
 */
function toYesNo(raw: string | null): 'si' | 'no' | null {
  if (raw === null) return null
  const text = normalize(raw)
  if (/^(si|yes|true|1)\b/.test(text)) return 'si'
  if (/^(no|false|0)\b/.test(text)) return 'no'
  return null
}

/** Une nombres en español: "A", "A y B", "A, B y C". */
function joinNames(names: readonly string[]): string | null {
  if (names.length === 0) return null
  if (names.length === 1) return names[0] ?? null
  const last = names[names.length - 1]
  return `${names.slice(0, -1).join(', ')} y ${last}`
}

/**
 * Alias de `evento` que cada categoría usa en sus plantillas. El resto de
 * alias queda en `null`: una plantilla de otra categoría no debe resolverlo.
 */
const CATEGORY_ALIAS: Readonly<
  Partial<Record<FaqCategory, 'obra' | 'festival' | 'experiencia' | 'prueba' | 'lugar'>>
> = {
  teatro: 'obra',
  festivales: 'festival',
  experiencias: 'experiencia',
  deporte: 'prueba',
  cultura: 'lugar',
}

/**
 * Resuelve todos los hechos de las plantillas de FAQ a partir del recurso de
 * la ficha. Función pura y total: nunca lanza ni devuelve cadenas vacías (todo
 * valor sin dato real es `null`). No sanea HTML (lo hace `renderFaqTemplate`)
 * ni decide qué preguntas se muestran.
 */
export function resolveFaqFacts(
  resource: PerformanceResourceItem,
  priceAmount: number | null,
  category: FaqCategory | null,
): FaqFacts {
  const evento = clean(resource.title)

  const artistLinks = resolveArtistLinks(resource).all
  const primerArtista = clean(artistLinks[0]?.name)
  const artista = primerArtista ?? (FAQ_ARTISTA_FALLBACK_TO_TITLE ? evento : null)
  const elenco = joinNames(
    artistLinks.map((artist) => clean(artist.name)).filter((name): name is string => name !== null),
  )

  const alias = category ? CATEGORY_ALIAS[category] : undefined

  const geo = clean(resource.venue?.city) ?? clean(resource.venue?.province)
  const recinto = clean(resource.venue?.name)
  const direccion = clean(resource.venue?.address)
  const ubicacion = recinto && direccion ? `${recinto}, ${direccion}` : null

  const subcategoria = clean(resolveProductGenre(resource.genres)?.style_name)
  const sinopsis = resource.synopsis ? clean(toPlainText(resource.synopsis)) : null

  const duracion = extraText(resource, EXTRA_SHOW_LENGTH)

  const edadMinima = parseAge(extraText(resource, EXTRA_AGES))
  const edadRecomendada = parseAge(extraText(resource, 'edad_recomendada'))

  const rango = formatFaqDateRange(resource.start_at, resource.end_at)

  return {
    evento,
    artista,
    obra: alias === 'obra' ? evento : null,
    festival: alias === 'festival' ? evento : null,
    experiencia: alias === 'experiencia' ? evento : null,
    prueba: alias === 'prueba' ? evento : null,
    lugar: alias === 'lugar' ? evento : null,
    geo,
    recinto,
    direccion,
    ubicacion,
    subcategoria,
    sinopsis,
    elenco,
    organizador: clean(resource.promoter?.name),
    duracion,
    horario: extraText(resource, 'show_times'),
    apertura: parseGates(extraText(resource, EXTRA_GATES)),
    fecha_inicio: rango?.inicio ?? null,
    fecha_fin: rango?.fin ?? null,
    precio_desde: typeof priceAmount === 'number' ? formatPriceDesde(priceAmount) : null,
    edad_minima: edadMinima === null ? null : String(edadMinima),
    edad_recomendada: edadRecomendada === null ? null : String(edadRecomendada),
    accesibilidad: parseAccessibility(extraText(resource, EXTRA_HANDICAPPED)),
    accesibilidad_detalle: extraText(resource, 'accesibilidad_detalle'),
    recogida: buildPickupList(resource),
    // Extras que el backend aún no manda: el `nicename` es el propio nombre del
    // hecho en snake_case, así que se activan solos cuando lleguen.
    nominativas: toYesNo(extraText(resource, 'nominativas')),
    zona_familiar: toYesNo(extraText(resource, 'zona_familiar')),
    politica_entrada_menores: extraText(resource, 'politica_entrada_menores'),
    cartel: extraText(resource, 'cartel'),
    idiomas: extraText(resource, 'idiomas'),
    requisitos: extraText(resource, 'requisitos'),
    distancia: extraText(resource, 'distancia'),
    recogida_dorsal: extraText(resource, 'recogida_dorsal'),
    horario_salida: extraText(resource, 'horario_salida'),
    tiempo_limite: extraText(resource, 'tiempo_limite'),
    que_incluye: extraText(resource, 'que_incluye'),
    horario_cierre: extraText(resource, 'horario_cierre'),
  }
}
